import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import { AgGridReact } from "ag-grid-react";

import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";

import {
  fetchWcPayouts,
  fetchWcProperties,
} from "../../../redux/features/sales/payoutsSlice";

const useStyles = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    minWidth: 260,
  },
}));

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const moneyFormatter = (params) =>
  params.value !== null && params.value !== undefined
    ? currencyFormatter.format(Number(params.value))
    : "";

const WcPayoutsPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { mode } = useSelector((state) => state.ui);
  const {
    wcPayoutsList,
    isLoadingWcPayouts,
    errorWcPayouts,
    wcProperties,
  } = useSelector((state) => state.payouts);

  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const gridApiRef = useRef(null);

  useEffect(() => {
    if (location.state?.showSuccess) {
      setSnackbar({
        open: true,
        message:
          location.state.snackbarContent || "Payout created successfully!",
        severity: "success",
      });
      history.replace(location.pathname, {});
    }
  }, [history, location]);

  useEffect(() => {
    dispatch(fetchWcProperties());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchWcPayouts({ accountId: selectedAccountId || undefined }));
  }, [dispatch, selectedAccountId]);

  const rowData = useMemo(() => wcPayoutsList || [], [wcPayoutsList]);
  const rowCount = rowData.length;
  const errorMessage = errorWcPayouts?.errorMessage || "";

  const ActionCellRenderer = useCallback(
    (params) => {
      const id = params.data?.payment_id;
      if (!id) return null;
      return (
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          color="primary"
          onClick={() => history.push(`/sales/woocommerce-payouts/${id}`)}
        >
          View
        </Button>
      );
    },
    [history]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Payout ID",
        field: "payment_id",
        filter: "agNumberColumnFilter",
        width: 120,
        pinned: "left",
      },
      {
        headerName: "Date",
        field: "created_at",
        minWidth: 180,
        valueFormatter: (params) =>
          params.value
            ? moment(params.value).tz("America/Chicago").format("lll")
            : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Property",
        field: "property_name",
        filter: "agTextColumnFilter",
        minWidth: 190,
      },
      {
        headerName: "Bank Account",
        field: "stripe_account_name",
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Orders Total",
        field: "total_orders_amount",
        type: "rightAligned",
        valueFormatter: moneyFormatter,
      },
      {
        headerName: "Commission",
        field: "commission_amount",
        type: "rightAligned",
        valueFormatter: moneyFormatter,
        cellStyle: { fontWeight: "bold" },
      },
      {
        headerName: "Transfer Amount",
        field: "total_paid",
        type: "rightAligned",
        valueFormatter: moneyFormatter,
        cellStyle: { color: "#19a86d", fontWeight: "bold" },
      },
      {
        headerName: "Status",
        field: "payment_status",
        filter: "agTextColumnFilter",
        valueFormatter: (params) =>
          params.value ? params.value.toUpperCase() : "",
        cellStyle: (params) =>
          params.value === "complete"
            ? {
                color: "white",
                backgroundColor: "#19a86dbb",
                fontWeight: "bold",
              }
            : undefined,
      },
      {
        headerName: "Comments",
        field: "comments",
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Stripe Transfer",
        field: "stripe_transfer_id",
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Actions",
        field: "payment_id",
        pinned: "right",
        width: 120,
        cellRendererFramework: ActionCellRenderer,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ],
    [ActionCellRenderer]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 120,
    }),
    []
  );

  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;
      if (isLoadingWcPayouts) params.api.showLoadingOverlay();
      else if (!rowCount || errorWcPayouts) params.api.showNoRowsOverlay();
    },
    [errorWcPayouts, isLoadingWcPayouts, rowCount]
  );

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (isLoadingWcPayouts) {
      api.showLoadingOverlay();
      return;
    }
    if (errorWcPayouts || rowCount === 0) {
      api.showNoRowsOverlay();
      return;
    }
    api.hideOverlay();
  }, [errorWcPayouts, isLoadingWcPayouts, rowCount]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} className={classes.headerRow}>
          <Typography variant="h6">WooCommerce Payouts</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => history.push("/sales/woocommerce-payouts/new")}
          >
            New Payout
          </Button>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.filterRow}>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.formControl}
            >
              <InputLabel>Filter by Property</InputLabel>
              <Select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                label="Filter by Property"
              >
                <MenuItem value="">All Properties</MenuItem>
                {wcProperties.map((p) => (
                  <MenuItem key={p.account_id} value={p.account_id}>
                    {p.staying_at}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div
            className={
              mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"
            }
            style={{ height: 600, width: "100%" }}
          >
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              enableCellTextSelection
              animateRows
              overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading payouts...</span>"
              overlayNoRowsTemplate={
                errorMessage
                  ? `<span class='ag-overlay-no-rows-center'>${errorMessage}</span>`
                  : "<span class='ag-overlay-no-rows-center'>No payouts to display</span>"
              }
              onGridReady={onGridReady}
            />
          </div>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default WcPayoutsPage;
